import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from '../shared/Button';
import { subscriptionService, SubscriptionType, SubscriptionLimits, SUBSCRIPTION_LIMITS } from '../../services/subscription/subscription.service';
import { getAuth } from 'firebase/auth';

// Hook React contextuel pour la gestion des abonnements
export function useSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionType>('free');
  const [limits, setLimits] = useState<SubscriptionLimits>(SUBSCRIPTION_LIMITS.free);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const maxRetries = 3;

  useEffect(() => {
    let isMounted = true;
    let retryTimeout: NodeJS.Timeout | null = null;
    
    const loadSubscription = async (retry = 0) => {
      try {
        setLoading(true);
        setError(null);
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        
        if (!userId) {
          // // // // // // // // console.log('Aucun utilisateur connecté, utilisation de l\'abonnement gratuit par défaut');
          setSubscription('free');
          setLimits(SUBSCRIPTION_LIMITS.free);
          setLoading(false);
          return;
        }
        
        try {
          const currentSub = await subscriptionService.getCurrentSubscription(userId);
          if (isMounted) {
            setSubscription(currentSub.type);
            setLimits(currentSub);
            setError(null);
            setRetryCount(0);
          }
        } catch (subError) {
          console.error(`Erreur lors de la récupération de l'abonnement (tentative ${retry + 1}/${maxRetries}):`, subError);
          
          if (retry < maxRetries && isMounted) {
            const delay = Math.min(1000 * Math.pow(2, retry), 10000); // Exponential backoff: 1s, 2s, 4s, ...
            // // // // // // // // console.log(`Nouvelle tentative dans ${delay / 1000} seconde(s)...`);
            
            setError(`Erreur lors du chargement des données d'abonnement. Nouvelle tentative ${retry + 1}/${maxRetries}...`);
            
            // En attendant la nouvelle tentative, utiliser l'abonnement gratuit
            setSubscription('free');
            setLimits(SUBSCRIPTION_LIMITS.free);
            
            // Planifier une nouvelle tentative
            retryTimeout = setTimeout(() => {
              setRetryCount(retry + 1);
              loadSubscription(retry + 1);
            }, delay);
          } else if (isMounted) {
            setError('Erreur lors du chargement des données d\'abonnement. Veuillez réessayer ultérieurement.');
            // En cas d'échec définitif, utiliser l'abonnement gratuit
            setSubscription('free');
            setLimits(SUBSCRIPTION_LIMITS.free);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    // Charger l'abonnement initialement
    loadSubscription();
    
    // Si le nombre de tentatives change (après un timeout), recharger l'abonnement
    if (retryCount > 0) {
      loadSubscription(retryCount);
    }
    
    return () => {
      isMounted = false;
      // Nettoyer le timeout si le composant est démonté
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [retryCount]);

  const reloadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        setSubscription('free');
        setLimits(SUBSCRIPTION_LIMITS.free);
        return;
      }
      
      const currentSub = await subscriptionService.getCurrentSubscription(userId);
      setSubscription(currentSub.type);
      setLimits(currentSub);
      setRetryCount(0);
    } catch (error) {
      console.error('Erreur lors du rechargement de l\'abonnement:', error);
      setError('Erreur lors du rechargement des données d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  return {
    subscription,
    limits,
    loading,
    error,
    retryCount,
    maxRetries,
    reload: reloadSubscription,
    hasFeature: (feature: keyof SubscriptionLimits['features']) => limits.features[feature],
    canManageMultipleEvents: () => limits.features.multipleEvents,
    getMaxEvents: () => limits.maxEvents,
    updateSubscription: async (userId: string, newSubscription: SubscriptionType) => {
      try {
        await subscriptionService.updateSubscription(userId, newSubscription);
        setSubscription(newSubscription);
        setLimits(SUBSCRIPTION_LIMITS[newSubscription]);
        setError(null);
        return true;
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'abonnement:', error);
        setError('Erreur lors de la mise à jour de l\'abonnement');
        throw error;
      }
    },
    checkLimit: async (limitType: keyof Omit<SubscriptionLimits, 'type' | 'features'>, currentCount: number) => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        
        if (!userId) {
          return {
            allowed: false,
            maxAllowed: SUBSCRIPTION_LIMITS.free[limitType]
          };
        }
        
        const result = await subscriptionService.checkLimit(userId, limitType, currentCount);
        return {
          allowed: !result.hasReachedLimit,
          maxAllowed: result.max
        };
      } catch (error) {
        console.error('Erreur lors de la vérification des limites:', error);
        // En cas d'erreur, utiliser les limites de l'abonnement gratuit
        return {
          allowed: currentCount < SUBSCRIPTION_LIMITS.free[limitType],
          maxAllowed: SUBSCRIPTION_LIMITS.free[limitType],
          error: true
        };
      }
    }
  };
}

// Composant HOC pour restreindre l'accès aux fonctionnalités selon l'abonnement
export function withSubscription<P>(
  WrappedComponent: React.ComponentType<P>,
  requiredFeature?: keyof SubscriptionLimits['features'],
  requiredSubscription?: SubscriptionType
) {
  return function WithSubscriptionComponent(props: P) {
    const { subscription, hasFeature, loading, error, reload, retryCount, maxRetries } = useSubscription();
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    useEffect(() => {
      if (loading) return;
      
      if (requiredSubscription) {
        // Vérifier si l'abonnement actuel est suffisant
        const subscriptionRank = { free: 0, essential: 1, pro: 2 };
        setHasAccess(subscriptionRank[subscription] >= subscriptionRank[requiredSubscription]);
      } else if (requiredFeature) {
        // Vérifier si la fonctionnalité est disponible
        setHasAccess(hasFeature(requiredFeature));
      } else {
        // Pas de restriction
        setHasAccess(true);
      }
    }, [subscription, loading, requiredFeature, requiredSubscription, hasFeature]);
    
    if (loading) {
      return (
        <div className="text-center p-4">
          {retryCount > 0 ? (
            <div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"></div>
              <div className="text-center">
                <p>{t('loading', { ns: 'common' })}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('retrying', { count: retryCount, max: maxRetries, ns: 'subscription' })}
                </p>
              </div>
            </div>
          ) : (
            <p>{t('loading', { ns: 'common' })}</p>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">{t('error', { ns: 'common' })}</h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300">{error}</p>
          <div className="space-y-2">
            <Button 
              variant="default"
              onClick={() => reload()}
              className="w-full"
            >
              {t('retry', { ns: 'common' })}
            </Button>
            
            <p className="text-sm text-gray-500 mt-2 dark:text-gray-400">
              {t('usingBasicFeatures', { ns: 'subscription' })}
            </p>
            
            <Button 
              variant="ghost"
              onClick={() => window.location.assign('mailto:support@wedbox.com')}
              className="text-sm"
            >
              {t('contactSupport', { ns: 'common' })}
            </Button>
          </div>
        </div>
      );
    }
    
    if (!hasAccess) {
      return (
        <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
          <LockClosedIcon className="h-12 w-12 mx-auto text-primary-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {t('featureRestricted', { ns: 'subscription' })}
          </h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300">
            {requiredFeature 
              ? t('upgradeToAccessFeature', { feature: t('features.' + requiredFeature, { ns: 'subscription' }), ns: 'subscription' })
              : requiredSubscription 
                ? t('upgradeToAccessPlan', { plan: t('plans.' + requiredSubscription, { ns: 'subscription' }), ns: 'subscription' })
                : t('featureRestricted', { ns: 'subscription' })
            }
          </p>
          <Button 
            variant="default"
            onClick={() => navigate('/subscription')}
          >
            {t('viewPlans', { ns: 'subscription' })}
          </Button>
        </div>
      );
    }
    
    return <WrappedComponent {...(props as any)} /> as any;
  };
}

// Composant pour afficher une alerte lorsqu'une limite est atteinte
export const LimitReachedAlert: React.FC<{
  limitType: keyof Omit<SubscriptionLimits, 'type' | 'features'>;
  currentCount: number;
  onUpgrade?: () => void;
}> = ({ limitType, currentCount, onUpgrade }) => {
  const { checkLimit, subscription } = useSubscription();
  const { t } = useTranslation();
  const [limitInfo, setLimitInfo] = useState<{ allowed: boolean; maxAllowed: number } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLimitInfo = async () => {
      const info = await checkLimit(limitType, currentCount);
      setLimitInfo(info);
    };
    
    checkLimitInfo();
  }, [limitType, currentCount, subscription, checkLimit]);

  if (!limitInfo || limitInfo.allowed) {
    return null;
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            {t('limitReached', { 
              limit: t('subscription.limits.' + limitType, { ns: 'subscription' }),
              count: currentCount,
              max: limitInfo.maxAllowed
            })}
          </h3>
          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
            <p>{t('upgradeToIncreaseLimits', { ns: 'subscription' })}</p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <Button
                variant="secondary"
                onClick={onUpgrade || (() => navigate('/subscription'))}
                className="bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
              >
                {t('viewPlans', { ns: 'subscription' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Nouveau composant pour gérer les événements multiples
export const MultipleEventsManager: React.FC<{
  events: Array<{ id: string; name: string }>;
  onCreateEvent: () => void;
  onSelectEvent: (eventId: string) => void;
}> = ({ events, onCreateEvent, onSelectEvent }) => {
  const { canManageMultipleEvents, getMaxEvents } = useSubscription();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canCreateMoreEvents = canManageMultipleEvents() && events.length < getMaxEvents();

  if (!canManageMultipleEvents() && events.length > 1) {
    // Situation anormale: l'utilisateur a plusieurs événements mais n'a pas le droit
    // Peut arriver si l'utilisateur a rétrogradé son abonnement
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              {t('multipleEventsRestricted', { ns: 'subscription' })}
            </h3>
            <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
              <p>{t('upgradeToProForMultipleEvents', { ns: 'subscription' })}</p>
            </div>
            <div className="mt-4">
              <div className="-mx-2 -my-1.5 flex">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/subscription')}
                  className="bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                >
                  {t('viewPlans', { ns: 'subscription' })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 mb-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">{t('yourEvents', { ns: 'events' })}</h2>
        {canCreateMoreEvents && (
          <Button
            variant="default"
            onClick={onCreateEvent}
            className="text-sm px-3 py-1"
          >
            {t('createNew', { ns: 'events' })}
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((event) => (
          <div 
            key={event.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
            onClick={() => onSelectEvent(event.id)}
          >
            <h3 className="font-medium">{event.name}</h3>
          </div>
        ))}
        
        {!canCreateMoreEvents && events.length < getMaxEvents() && (
          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col items-center justify-center text-center text-gray-500 dark:text-gray-400">
            <LockClosedIcon className="h-8 w-8 mb-2" />
            <p>{t('upgradeToCreateMoreEvents', { ns: 'events' })}</p>
            <Button
              variant="ghost"
              onClick={() => navigate('/subscription')}
              className="mt-2 text-sm"
            >
              {t('viewPlans', { ns: 'subscription' })}
            </Button>
          </div>
        )}
      </div>
      
      {events.length === getMaxEvents() && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('events.maxEventsReached', { max: getMaxEvents(), ns: 'events' })}
        </p>
      )}
    </div>
  );
}; 